import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { strongSelectMenuProps } from "components/common/dropdownPaper";
import {
  clientFacingRunId,
  isRunExecuting,
  rerunAwaitsConfirmation,
} from "features/integrationsBeta/clientRunStatus";
import type { IntegrationRunListItem } from "features/integrationsBeta/integrationsBeta";
import { useState } from "react";

export interface RunActionHandlers {
  onSetClientStatus: (run: IntegrationRunListItem) => void;
  onRerun: (run: IntegrationRunListItem) => void;
  /**
   * Cross-link into the Client history tab: the run the client sees in place
   * of this one (itself, or the original a re-run serviced).
   */
  onOpenClientHistory: (runId: number) => void;
  /** Jump to another run of this integration (original / latest re-run). */
  onOpenRun: (runId: number) => void;
}

type Props = RunActionHandlers & {
  run: IntegrationRunListItem;
  /** Manage: shows Set client status. */
  canEdit: boolean;
  /** Contribute: shows Re-run. */
  canRun: boolean;
  statusLabel: (value: string | null | undefined) => string;
};

const IN_PROGRESS_HINT = "Available once the run finishes";
const RERUN_HINT =
  "Re-runs are not shown to the client - set it on the original";

/**
 * Per-run servicing menu (kebab) of the internal history - in the table row
 * and in the drill-down header. Setting the client status is disabled on a run
 * the client never sees (a re-run) and, like Re-run, while the run is still
 * executing - the API answers 409 in both cases; a run whose latest re-run
 * succeeded nudges the operator to confirm the client status.
 */
export default function RunActionsMenu({
  run,
  canEdit,
  canRun,
  statusLabel,
  onSetClientStatus,
  onRerun,
  onOpenClientHistory,
  onOpenRun,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  const executing = isRunExecuting(run);
  const latestRerunId = run.latest_rerun_id;
  const originalRunId = run.original_run;
  const clientRunId = clientFacingRunId(run);
  const clientStatusHint = !run.client_visible
    ? RERUN_HINT
    : executing
      ? IN_PROGRESS_HINT
      : rerunAwaitsConfirmation(run)
        ? "Latest re-run succeeded - confirm it"
        : undefined;

  const pick = (action: () => void) => () => {
    setAnchor(null);
    action();
  };

  return (
    <>
      <Tooltip title="Run actions">
        <IconButton
          size="small"
          aria-label="Run actions"
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={strongSelectMenuProps.PaperProps}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canEdit && (
          <MenuItem
            disabled={executing || !run.client_visible}
            onClick={pick(() => onSetClientStatus(run))}
          >
            <ListItemIcon>
              <EditNoteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Set client status..."
              secondary={clientStatusHint}
            />
          </MenuItem>
        )}
        {canRun && (
          <MenuItem disabled={executing} onClick={pick(() => onRerun(run))}>
            <ListItemIcon>
              <ReplayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Re-run"
              secondary={executing ? IN_PROGRESS_HINT : undefined}
            />
          </MenuItem>
        )}
        {latestRerunId !== null && (
          <MenuItem onClick={pick(() => onOpenRun(latestRerunId))}>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Open latest re-run"
              secondary={statusLabel(run.latest_rerun_status)}
            />
          </MenuItem>
        )}
        {originalRunId !== null && (
          <MenuItem onClick={pick(() => onOpenRun(originalRunId))}>
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open original run" />
          </MenuItem>
        )}
        {clientRunId !== null && (
          <MenuItem onClick={pick(() => onOpenClientHistory(clientRunId))}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                run.client_visible
                  ? "Open in client history"
                  : "Open original in client history"
              }
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
